/**
 * Web panel entry point.
 *
 * Wires up:
 *   - @fontsource-variable/geist + geist-mono for typography
 *   - Tailwind v4 global styles with @theme tokens
 *   - TanStack QueryClient with 60s staleTime + refetchOnWindowFocus (D-11)
 *   - React Router via RouterProvider
 *   - Sonner Toaster for toast notifications (D-21)
 */
import '@fontsource-variable/geist';
import '@fontsource-variable/geist-mono';
import './styles/globals.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router';
import { Toaster } from 'sonner';
import { router } from './router';

/**
 * QueryClient for the web panel (D-11).
 * - 60s staleTime matches the polling cadence for dashboard/list views.
 * - refetchOnWindowFocus=true ensures data refreshes when operator returns.
 * - 401 errors skip retry (handled by API client refresh dedup).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        return status === 401 ? false : failureCount < 2;
      },
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('root element missing');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5_000,
          style: {
            fontFamily: 'var(--font-sans)',
          },
        }}
      />
    </QueryClientProvider>
  </StrictMode>,
);
